// The help system's content and lookups (HELP_SYSTEM_PLAN.md, phase 1).
import { ARTICLES as EN_ARTICLES, CATEGORIES as EN_CATEGORIES } from "./articles.en";
import * as ES from "./articles.es-419";
import * as FR from "./articles.fr-CA";
import * as PT from "./articles.pt-BR";

// Translations (decision D3): AI-drafted from the reviewed English on 2026-11-01, each
// marked REVIEW_STATUS "ai_draft" until a native speaker reviews it. A language without
// a translation shows the English with a short notice.
const BY_LANG = {
  en: { articles: EN_ARTICLES, categories: EN_CATEGORIES },
  "pt-BR": { articles: PT.ARTICLES, categories: PT.CATEGORIES },
  "es-419": { articles: ES.ARTICLES, categories: ES.CATEGORIES },
  "fr-CA": { articles: FR.ARTICLES, categories: FR.CATEGORIES },
};

export function helpContent(lang) {
  const translated = BY_LANG[lang];
  return {
    articles: translated?.articles || EN_ARTICLES,
    categories: translated?.categories || EN_CATEGORIES,
    translated: Boolean(translated),
  };
}

// Which screen the candidate is on, for "For this screen".
export function currentScreen(pathname, doc = typeof document !== "undefined" ? document : null) {
  if (pathname === "/login") return "login";
  if (pathname === "/upload") return "upload";
  if (pathname === "/account" || pathname === "/setup") return "account";
  if (pathname === "/downloads") return "downloads";
  if (/^\/prepare\/[^/]+$/.test(pathname)) return "prepare";
  if (/^\/interview\/[^/]+$/.test(pathname)) {
    if (doc?.querySelector('[data-testid="live-interview"]')) return "live";
    if (doc?.querySelector('[data-testid="warmup-gate"]')) return "warmup";
    return "interview";
  }
  if (/^\/report\/[^/]+$/.test(pathname)) {
    return doc?.querySelector('[data-testid="trial-report-preview"]') ? "trial-report" : "report";
  }
  return "general";
}

const GENERAL = ["how-it-works", "guided-or-live", "check-sound", "access-sessions"];

export function articlesForScreen(articles, screen, max = 4) {
  const picked = screen === "general"
    ? GENERAL.map((id) => articles.find((a) => a.id === id)).filter(Boolean)
    : articles.filter((a) => (a.screens || []).includes(screen));
  return picked.slice(0, max);
}

// [text](#article-id) links between articles (2026-10-31).
export const LINK_RE = /\[([^\]]+)\]\(#([a-z0-9-]+)\)/g;

/** Text and links of one line: [{ text }, { text, articleId }, …]. */
export function splitLinks(line) {
  const out = [];
  let last = 0;
  for (const m of String(line || "").matchAll(LINK_RE)) {
    if (m.index > last) out.push({ text: line.slice(last, m.index) });
    out.push({ text: m[1], articleId: m[2] });
    last = m.index + m[0].length;
  }
  if (last < String(line || "").length) out.push({ text: line.slice(last) });
  return out;
}

export const plainText = (line) => String(line || "").replace(LINK_RE, "$1");

function articleText(a) {
  const parts = [a.title];
  for (const b of a.body || []) {
    if (b.p) parts.push(b.p);
    if (b.note) parts.push(b.note);
    for (const item of b.steps || b.list || []) parts.push(item);
  }
  return plainText(parts.join(" ")).toLowerCase();
}

const fold = (s) => s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

/** Articles matching every word of the query; title matches first. */
export function searchArticles(articles, query) {
  const words = fold(query || "").split(/\s+/).filter((w) => w.length > 1);
  if (!words.length) return [];
  return articles
    .map((a) => {
      const title = fold(a.title);
      const text = fold(articleText(a));
      if (!words.every((w) => text.includes(w))) return null;
      return { a, score: words.filter((w) => title.includes(w)).length };
    })
    .filter(Boolean)
    .sort((x, y) => y.score - x.score)
    .map((r) => r.a);
}

/** Open the help panel from anywhere, optionally at one article. */
export function openHelp(articleId) {
  window.dispatchEvent(new CustomEvent("mec:open-help", { detail: { articleId } }));
}
